import { BrowserModule, HammerGestureConfig, HammerModule, HAMMER_GESTURE_CONFIG } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, APP_INITIALIZER, Injectable } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrModule } from 'ngx-toastr';
import { NgChartsModule } from 'ng2-charts';
import { GoogleApiModule, NgGapiClientConfig, NG_GAPI_CONFIG } from 'ng-gapi';
import { AppConfig } from './helpers/app.config';
import { environment } from '../environments/environment';
import { catchError, EMPTY, forkJoin, map, of, switchMap } from 'rxjs';

// Redux
import { StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { reducers, metaReducers } from './reducers';

// Routes
import { AppRoutingModule } from './app-routing.module';

// interceptors
import { LoadingInterceptor } from './interceptors/loading.interceptor';
import { JwtInterceptor } from './interceptors/jwt.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';

// Components
import { AppComponent } from './app.component';
import { ErrorComponent } from './components/error/error.component';

// Consultant
import { LoginComponent } from './components/consultant/login/login.component';
import { BoardingConsultantComponent } from './components/consultant/boarding-consultant/boarding-consultant.component';
import { CandidatesComponent } from './components/consultant/candidates/candidates.component';
import { OfficeSelectorComponent } from './components/consultant/office-selector/office-selector.component';
import { InviteComponent } from './components/consultant/invite/invite.component';
import { CreateCandidateComponent } from './components/consultant/create-candidate/create-candidate.component';
import { UploadCandidatesComponent } from './components/consultant/upload-candidates/upload-candidates.component';
import { HistoryCandidatesComponent } from './components/consultant/history/history.component';
import { ProcessesComponent } from './components/consultant/processes/processes.component';
import { ProcessCreateComponent } from './components/consultant/process-create/process-create.component';
import { ProcessDetailComponent } from './components/consultant/process-detail/process-detail.component';
import { ReportsCtComponent } from './components/consultant/reports-ct/reports-ct.component';
import { IdealProfileDetailComponent } from './components/consultant/ideal-profile-detail/ideal-profile-detail.component';
import { IdealProfilesComponent } from './components/consultant/ideal-profiles/ideal-profiles.component';
import { TestSearchComponent } from './components/consultant/test-search/test-search.component';

// Candidate
import { HomeComponent } from './components/candidate/common/home/home.component';
import { TestComponent } from './components/candidate/test/test.component';

import { AdnInstructionComponent } from './components/candidate/adn/adn-instruction/adn-instruction.component';
import { AdnExamplesComponent } from './components/candidate/adn/adn-examples/adn-examples.component';
import { EnglishInstructionComponent } from './components/candidate/english-test/english-instruction/english-instruction.component';
import { ReasoningInstructionComponent } from './components/candidate/reasoning-test/reasoning-instruction/reasoning-instruction.component';
import { ReasoningExampleComponent } from './components/candidate/reasoning-test/reasoning-example/reasoning-example.component';
import { DigitalInstructionComponent } from './components/candidate/digital-test/digital-instruction/digital-instruction.component';

import { AdnQuestionComponent } from './components/candidate/adn/adn-question/adn-question.component';
import { QuestionComponent } from './components/candidate/common/question/question.component';
import { DigitalQuestionComponent } from './components/candidate/digital-test/digital-question/digital-question.component';
import { LevelCompletedComponent } from './level-completed/level-completed.component';
import { KoLevelTimeComponent } from './components/candidate/common/ko-level-time/ko-level-time.component';
import { FinalizedComponent } from './components/candidate/common/finalized/finalized.component';
import { FeedbackComponent } from './components/candidate/common/feedback/feedback.component';

// Reports
import { CoverPageReportComponent } from './components/report/cover-page-report/cover-page-report.component';
import { LastPageReportComponent } from './components/report/last-page-report/last-page-report.component';
import { AdnReportComponent } from './components/report/adn-report/adn-report.component';
import { EvalualiaReportComponent } from './components/report/evalualia-report/evalualia-report.component';
import { MindsetReportComponent } from './components/report/mindset-report/mindset-report.component';
import { CtReportComponent } from './components/report/ct-report/ct-report.component';
import { LanguageReportComponent } from './components/report/language-report/language-report.component';
import { ReasoningReportComponent } from './components/report/reasoning-report/reasoning-report.component';
import { DigitalReportComponent } from './components/report/digital-report/digital-report.component';

// Layout
import { ConsultantHeaderComponent } from './components/layout/consultant-header/consultant-header.component';
import { CandidateHeaderComponent } from './components/layout/candidate-header/candidate-header.component';
import { FooterComponent } from './components/layout/footer/footer.component';

// Shared
import { ControlMessagesComponent } from './components/shared/control-messages/control-messages.component';
import { DatatableComponent } from './components/shared/datatable/datatable.component';
import { LevelBarComponent } from './components/shared/level-bar/level-bar.component';
import { LoadingComponent } from './components/shared/loading/loading.component';

// Pipes
import { FilterPipe } from './pipes/filter.pipe';
import { CheckChildrenPipe } from './pipes/check-children.pipe';
import { TestModelPipe } from './pipes/test-model.pipe';
import { OfficePipe } from './pipes/office.pipe';
import { DateFormatPipe } from './pipes/date-format.pipe';
import { InitialsPipe } from './pipes/initials.pipe';
import { SecondsTimePipe } from './pipes/seconds-time.pipe';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { TrimDirective } from './directives/trim.directive';
import { CustomDatePipe } from './pipes/custom-date.pipe';

// Para animaciones, se necesitan los 2 paquetes
import { LottieModule } from 'ngx-lottie';
import player from 'lottie-web';

// eventos swipe
import * as Hammer from 'hammerjs';
import { SafePipe } from './pipes/safe.pipe';
import { ExportReportsComponent } from './components/consultant/export-reports/export-reports.component';
import { ExportScoresComponent } from './components/consultant/export-scores/export-scores.component';
import { MassiveSendComponent } from './components/consultant/massive-send/massive-send.component';
import { VersionComponent } from './components/version/version.component';
import { LoginGoogleComponent } from './components/consultant/login-google/login-google.component';

import { LegalModalComponent } from './components/candidate/legal/legal-modal/legal-modal.component';
import { LegalTermsComponent } from './components/candidate/legal/legal-terms/legal-terms.component';
import { PrivacyComponent } from './components/candidate/legal/privacy/privacy.component';
import { ExpiredComponent } from './components/candidate/common/expired/expired.component';
import { IndexReportComponent } from './components/report/index-report/index-report.component';


// Note we need a separate function as it's required
// by the AOT compiler.
export function playerFactory() {
  return player;
}
const gapiClientConfig: NgGapiClientConfig = {
  fetch_basic_profile: true,
  cookie_policy: 'single_host_origin',
  client_id: '53313086075-l499tnp0j6d3unsj4e79nepp5rdgg784.apps.googleusercontent.com',
  ux_mode: 'popup',
  discoveryDocs: ['https://analyticsreporting.googleapis.com/$discovery/rest?version=v4'],
  scope: 'profile email'
};

function initializeApp(appConfig: AppConfig) {
  return () => appConfig.load();
}

export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) { }

  getTranslation(lang: string): any {
    let resources = ['./assets/i18n/', './assets/i18n/adn/'];
    if (lang.includes('projects')) {
      resources.push(AppConfig.settings.API + '/s3/translations/', AppConfig.settings.API + '/s3/translations/adn__');
    }

    return forkJoin(resources.map(path => {
      path += (path.includes('/s3/') ? lang : lang.split('__')[lang.split('__').length - 1]) + '.json';
      return this.http.get(path, { responseType: 'text' }).pipe(switchMap((s3Url: string) => {
        if (path.includes('/s3/')) {
          return this.http.get(s3Url);
        } else {
          return of(JSON.parse(s3Url));
        }
      }));
    })).pipe(map((response: any[]) => {
      if (response[0]) {
        return response.reduce((a, b) => {
          return Object.assign(a, b);
        });
      }
    }));
  }
}

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  overrides = {
    swipe: { direction: Hammer.DIRECTION_ALL },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    ErrorComponent,
    LoginComponent,
    BoardingConsultantComponent,
    CandidatesComponent,
    OfficeSelectorComponent,
    InviteComponent,
    CreateCandidateComponent,
    UploadCandidatesComponent,
    HistoryCandidatesComponent,
    ProcessesComponent,
    ProcessCreateComponent,
    ProcessDetailComponent,
    ReportsCtComponent,
    IdealProfileDetailComponent,
    IdealProfilesComponent,
    HomeComponent,
    EvalualiaReportComponent,
    MindsetReportComponent,
    CtReportComponent,
    LanguageReportComponent,
    ReasoningReportComponent,
    DigitalReportComponent,
    ConsultantHeaderComponent,
    CandidateHeaderComponent,
    FooterComponent,
    ControlMessagesComponent,
    DatatableComponent,
    LevelBarComponent,
    LoadingComponent,
    OfficePipe,
    DateFormatPipe,
    FilterPipe,
    CheckChildrenPipe,
    TestModelPipe,
    InitialsPipe,
    SecondsTimePipe,
    CustomDatePipe,
    ProcessesComponent,
    ProcessCreateComponent,
    ProcessDetailComponent,
    IdealProfileDetailComponent,
    IdealProfilesComponent,
    TestSearchComponent,
    ClickOutsideDirective,
    TrimDirective,
    TestComponent,
    EnglishInstructionComponent,
    QuestionComponent,
    FinalizedComponent,
    LegalModalComponent,
    SafePipe,
    KoLevelTimeComponent,
    ExportReportsComponent,
    ExportScoresComponent,
    MassiveSendComponent,
    VersionComponent,
    ReasoningInstructionComponent,
    ReasoningExampleComponent,
    FeedbackComponent,
    DigitalInstructionComponent,
    DigitalQuestionComponent,
    LoginGoogleComponent,
    AdnInstructionComponent,
    AdnQuestionComponent,
    AdnExamplesComponent,
    AdnReportComponent,
    LegalTermsComponent,
    PrivacyComponent,
    ExpiredComponent,
    LastPageReportComponent,
    CoverPageReportComponent,
    LevelCompletedComponent,
    IndexReportComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    NgbModule,
    NgSelectModule,
    NgChartsModule,
    HammerModule,
    ToastrModule.forRoot(),
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreRouterConnectingModule.forRoot(),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    GoogleApiModule.forRoot({
      provide: NG_GAPI_CONFIG,
      useValue: gapiClientConfig
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
        deps: [HttpClient]
      }
    }),
    LottieModule.forRoot({ player: playerFactory })
  ],
  providers: [
    AppConfig,
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [AppConfig], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoadingInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HAMMER_GESTURE_CONFIG, useClass: HammerConfig },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
